import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Space, Spin, Table, Tag, Typography, message } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { serverFetch } from "../../core/server";
import "./database-blocks.css";

const KIND_LABEL = {
  Create: "Добавить",
  Alter: "Изменить",
  Drop: "Удалить"
};

const KIND_COLOR = {
  Create: "green",
  Alter: "blue",
  Drop: "red"
};

const DatabaseSchema = () => {
  const [status, setStatus] = useState(null);
  const [changes, setChanges] = useState([]);
  const [errors, setErrors] = useState([]);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const result = await serverFetch("app/database/schema");
      setStatus(result);
    } catch (e) {
      setStatus(null);
      message.error(e.userMessage || e.message || "Не удалось получить версию структуры");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const checkSchema = async () => {
    setOperation("check");
    try {
      const result = await serverFetch("app/database/schema/check", { method: "POST" });
      setStatus(result);
      setChanges(Array.isArray(result?.changes) ? result.changes : []);
      setErrors([]);
      setChecked(true);
      const count = Array.isArray(result?.changes) ? result.changes.length : 0;
      message.success(count ? `Найдено изменений: ${count}` : "Структура совпадает с эталоном");
    } catch (e) {
      message.error(e.userMessage || e.message || "Не удалось проверить структуру");
    } finally {
      setOperation("");
    }
  };

  const updateSchema = () => {
    const dropCount = changes.filter((item) => item.kind === "Drop" || item.isDestructive).length;
    Modal.confirm({
      title: "Обновить структуру базы?",
      content: dropCount
        ? `Будет выполнено ${changes.length} изменений, из них опасных (удаление/смена типа): ${dropCount}.`
        : changes.length
          ? `Будет выполнено ${changes.length} изменений.`
          : "Скрипт будет построен заново и выполнен. Операция может занять несколько минут.",
      okText: "Обновить",
      okButtonProps: { danger: dropCount > 0 },
      cancelText: "Отмена",
      onOk: async () => {
        setOperation("update");
        try {
          const result = await serverFetch("app/database/schema/update", { method: "POST" });
          setStatus(result);
          setChanges(Array.isArray(result?.changes) ? result.changes : []);
          setErrors(Array.isArray(result?.errors) ? result.errors : []);
          setChecked(true);
          if (result?.errors?.length) {
            message.warning(`Обновление завершено с ошибками: ${result.errors.length}`);
          } else {
            message.success(result?.skipped ? result.skipReason || "Обновление не требуется" : "Структура обновлена");
          }
        } catch (e) {
          message.error(e.userMessage || e.message || "Не удалось обновить структуру");
          return Promise.reject();
        } finally {
          setOperation("");
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        title: "Действие",
        dataIndex: "kind",
        width: 120,
        render: (kind) => <Tag color={KIND_COLOR[kind] || "default"}>{KIND_LABEL[kind] || kind}</Tag>
      },
      {
        title: "Объект",
        dataIndex: "objectName",
        width: 240,
        render: (name, row) => (
          <span>
            <Typography.Text type="secondary">{row.objectType} </Typography.Text>
            {name}
          </span>
        )
      },
      {
        title: "SQL",
        dataIndex: "sql",
        render: (sql) => (
          <Typography.Text code className="db-schema-sql">
            {sql}
          </Typography.Text>
        )
      }
    ],
    []
  );

  return (
    <div>
      <div className="db-toolbar">
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={loadStatus} loading={loading && !operation}>
            Версия
          </Button>
          <Button onClick={checkSchema} loading={operation === "check"} disabled={!!operation && operation !== "check"}>
            Проверить
          </Button>
          <Button
            type="primary"
            onClick={updateSchema}
            loading={operation === "update"}
            disabled={!!operation && operation !== "update"}
          >
            Обновить
          </Button>
        </Space>
      </div>

      <Spin spinning={loading || !!operation}>
        {status ? (
          <Typography.Paragraph>
            Версия программы: <strong>{status.programVersion || "—"}</strong>
            {" · "}
            Версия структуры базы: <strong>{status.databaseVersion || "—"}</strong>
            {status.needsUpdate ? (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                Нужно обновление
              </Tag>
            ) : (
              <Tag color="green" style={{ marginLeft: 8 }}>
                Актуальна
              </Tag>
            )}
          </Typography.Paragraph>
        ) : null}

        {errors.length > 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Часть операторов не выполнилась"
            description={
              <ul className="db-schema-errors">
                {errors.map((item, index) => (
                  <li key={index}>{item.message}</li>
                ))}
              </ul>
            }
          />
        ) : null}

        {checked && changes.length === 0 ? (
          <Typography.Paragraph type="secondary">Различий с эталоном basa.sql нет.</Typography.Paragraph>
        ) : null}

        {changes.length > 0 ? (
          <Table
            size="small"
            rowKey={(_, index) => String(index)}
            pagination={changes.length > 50 ? { pageSize: 50 } : false}
            columns={columns}
            dataSource={changes}
            rowClassName={(row) => (row.isDestructive ? "db-schema-row--danger" : "")}
          />
        ) : null}
      </Spin>
    </div>
  );
};

export default DatabaseSchema;
